import { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle, Users, MapPin, Calendar, User } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARCHETYPES = ["Explorer", "Romantic", "Celebrator", "Seeker", "Sensorialist", "Soul Traveler"];
const TRAVELER_COUNTS = ["1", "2", "3–5", "6–10", "10+"];
const TRAVEL_TYPES = ["Solo", "Couple", "Group", "Family", "Corporate"];

// Module-scope — prevents unstable component reference on each render
function PillGroup({ options, field, currentValue, onSelect, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          data-testid={`${testPrefix}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
          onClick={() => onSelect(field, currentValue === opt ? "" : opt)}
          className={`text-xs px-3 py-1.5 rounded-sm border transition-all duration-150 ${
            currentValue === opt
              ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]"
              : "border-white/10 text-[#A1A1AA] hover:border-white/25 hover:text-white"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export default function UniversalEnquiryModal({ isOpen, onClose, prefillExperience, prefillArchetype, source = "platform" }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    preferred_experience: prefillExperience || "",
    archetype: prefillArchetype || "",
    number_of_travelers: "", travel_type: "",
    preferred_destinations: "", preferred_dates: "",
    message: "", source
  });
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");

  // Sync prefills when props change
  useEffect(() => {
    if (isOpen) {
      const storedArch = localStorage.getItem("travelorza_archetype") || "";
      setForm(f => ({
        ...f,
        preferred_experience: prefillExperience || f.preferred_experience,
        archetype: prefillArchetype || storedArch || f.archetype,
        source
      }));
      setStatus(null);
      setError("");
    }
  }, [isOpen, prefillExperience, prefillArchetype, source]);

  if (!isOpen) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async e => {
    e.preventDefault();
    // Inline validation
    if (!form.name.trim()) { setError("Full name is required."); return; }
    if (!form.phone.trim()) { setError("WhatsApp number is required."); return; }
    if (form.email && !validateEmail(form.email)) { setError("Please enter a valid email address."); return; }
    setStatus("loading"); setError("");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      // Signal Orza to continue with context after form closes
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("enquiry_submitted", { detail: { ...form } }));
      }, 2500);
    } catch {
      setStatus("error");
      setError("Something went wrong. Please try again or reach us on WhatsApp.");
    }
  };

  // (PillGroup component defined at module scope above)

  return (
    <div
      data-testid="universal-enquiry-overlay"
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-6"
      style={{ background: "rgba(0,0,0,0.88)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid="universal-enquiry-modal"
        className="w-full md:max-w-2xl glass rounded-t-2xl md:rounded-xl overflow-hidden"
        style={{ maxHeight: "92vh", overflowY: "auto" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 md:p-6 border-b border-white/5 sticky top-0 glass z-10">
          <div>
            <p className="overline mb-0.5">Travelorza</p>
            <h3 className="font-heading text-2xl font-light text-white">Design My Journey</h3>
          </div>
          <button data-testid="enquiry-close-btn" onClick={onClose} className="text-[#71717A] hover:text-white p-1.5">
            <X size={18} />
          </button>
        </div>

        {status === "success" ? (
          <div data-testid="enquiry-success" className="p-8 md:p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/30 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="text-[#C9A84C]" size={24} />
            </div>
            <h4 className="font-heading text-2xl font-light text-white mb-2">Your journey has been submitted</h4>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-2">Your dedicated Experience Architect will reach out within 24 hours to begin designing your transformation.</p>
            <p className="text-xs text-[#71717A] mb-6">Check your WhatsApp for a confirmation message.</p>
            <button data-testid="enquiry-done-btn" onClick={onClose} className="btn-gold text-xs px-10 py-3">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 md:p-6 space-y-6">
            {/* Section 1: Your Details */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-4 flex items-center gap-2">
                <User size={10} />Your Details
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Full Name <span className="text-[#C9A84C]">*</span></label>
                  <input
                    data-testid="enquiry-name"
                    type="text" name="name" value={form.name}
                    onChange={e => set("name", e.target.value)}
                    placeholder="Your full name"
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">WhatsApp Number <span className="text-[#C9A84C]">*</span></label>
                  <input
                    data-testid="enquiry-phone"
                    type="tel" name="phone" value={form.phone}
                    onChange={e => set("phone", e.target.value)}
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Email <span className="text-[#A1A1AA]">(optional)</span></label>
                <input
                  data-testid="enquiry-email"
                  type="email" name="email" value={form.email}
                  onChange={e => set("email", e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
                />
              </div>
            </div>

            <div className="h-px bg-white/5" />

            {/* Section 2: Your Journey */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-4 flex items-center gap-2">
                <MapPin size={10} />Your Journey
              </p>
              <div className="space-y-5">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2.5">Number of Travelers</label>
                  <PillGroup options={TRAVELER_COUNTS} field="number_of_travelers" currentValue={form.number_of_travelers} onSelect={set} testPrefix="travelers" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2.5">Travel Type</label>
                  <PillGroup options={TRAVEL_TYPES} field="travel_type" currentValue={form.travel_type} onSelect={set} testPrefix="travel-type" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Preferred Experience</label>
                    <input
                      data-testid="enquiry-preferred-experience"
                      type="text" value={form.preferred_experience}
                      onChange={e => set("preferred_experience", e.target.value)}
                      placeholder="e.g. Aurora expedition, Bali dining..."
                      className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Destinations in Mind</label>
                    <input
                      data-testid="enquiry-destinations"
                      type="text" value={form.preferred_destinations}
                      onChange={e => set("preferred_destinations", e.target.value)}
                      placeholder="e.g. Maldives, Japan, Europe..."
                      className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">
                      <Calendar size={9} className="inline mr-1" />Preferred Dates
                    </label>
                    <input
                      data-testid="enquiry-dates"
                      type="text" value={form.preferred_dates}
                      onChange={e => set("preferred_dates", e.target.value)}
                      placeholder="e.g. March 2025, Flexible..."
                      className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Your Soulprint <span className="text-[#A1A1AA]">(if known)</span></label>
                    <select
                      data-testid="enquiry-archetype"
                      value={form.archetype}
                      onChange={e => set("archetype", e.target.value)}
                      className="w-full bg-[#111111] border border-white/10 focus:border-[#C9A84C] outline-none text-white py-2 px-3 text-sm rounded-sm appearance-none"
                    >
                      <option value="">Not sure yet</option>
                      {ARCHETYPES.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="h-px bg-white/5" />

            {/* Section 3: Tell Us More */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Anything else you&apos;d like us to know?</label>
              <textarea
                data-testid="enquiry-message"
                value={form.message}
                onChange={e => set("message", e.target.value)}
                rows={3}
                placeholder="Tell us about your dream journey — the feeling, the people, the transformation you're searching for..."
                className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm resize-none transition-colors leading-relaxed"
              />
            </div>

            {error && (
              <div data-testid="enquiry-error" className="flex items-center gap-2 text-red-400 text-xs">
                <AlertCircle size={13} />{error}
              </div>
            )}

            <button
              data-testid="enquiry-submit-btn"
              type="submit"
              disabled={status === "loading"}
              className="w-full btn-gold py-4 text-xs disabled:opacity-40"
            >
              {status === "loading" ? "Submitting..." : "Design My Journey"}
            </button>

            <p className="text-center text-[#71717A] text-[10px] tracking-wider">
              Your Experience Architect will respond within 24 hours · All data is kept confidential
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
